<script setup>
import { ref, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import uazapiChannelAPI from 'dashboard/api/channel/uazapiChannel';

const router = useRouter();

// Estados do Wizard
const step = ref(1); // 1 = Config, 2 = QRCode, 3 = Sucesso
const isLoading = ref(false);
const showToken = ref(false);
const pollingInterval = ref(null);

// Dados do Formulário
const inboxName = ref('');
const instanceName = ref('');
const apiHost = ref('https://free.uazapi.com');
const apiKey = ref('');

// Dados da Conexão
const createdInbox = ref(null);
const qrcodeImage = ref('');

// 1. Gera o QR Code na UazAPI sem salvar no banco de dados
const generateQRCode = async () => {
  if (!inboxName.value.trim()) {
    useAlert('Por favor, informe um nome para a Caixa de Entrada.');
    return;
  }
  if (!instanceName.value.trim()) {
    useAlert('Por favor, informe o Nome da Instância gerada na UazAPI (Ex: IIK4ZN).');
    return;
  }
  if (!apiHost.value.trim()) {
    useAlert('Por favor, informe a URL da UazAPI.');
    return;
  }
  if (!apiKey.value.trim()) {
    useAlert('Por favor, informe o Token da Instância da UazAPI.');
    return;
  }

  isLoading.value = true;
  try {
    const response = await uazapiChannelAPI.connectPreview({
      api_host: apiHost.value.trim(),
      api_key: apiKey.value.trim(),
      instance_name: instanceName.value.trim(),
    });

    qrcodeImage.value = response.data?.qrcode || '';

    if (response.data?.connected === true || response.data?.status === 'open') {
      await finalizeInboxCreation(response.data?.instance?.owner);
    } else {
      step.value = 2;
      startPollingStatus();
    }
  } catch (error) {
    console.error('Erro ao conectar à UazAPI:', error);
    useAlert(error.response?.data?.error || 'Erro ao conectar à UazAPI.');
  } finally {
    isLoading.value = false;
  }
};

// 2. Atualizar QR Code sob demanda
const refreshQRCode = async () => {
  isLoading.value = true;
  try {
    const response = await uazapiChannelAPI.connectPreview({
      api_host: apiHost.value.trim(),
      api_key: apiKey.value.trim(),
      instance_name: instanceName.value.trim(),
    });

    if (response.data?.qrcode) {
      qrcodeImage.value = response.data.qrcode;
    }
    if (response.data?.connected === true || response.data?.status === 'open') {
      await finalizeInboxCreation(response.data?.instance?.owner);
    }
  } catch (error) {
    console.error('Erro ao atualizar QR Code:', error);
  } finally {
    isLoading.value = false;
  }
};

// 3. Polling para checar se o celular leu o QR Code
const checkStatusManually = async () => {
  isLoading.value = true;
  try {
    const response = await uazapiChannelAPI.checkPreviewStatus({
      api_host: apiHost.value.trim(),
      api_key: apiKey.value.trim(),
      instance_name: instanceName.value.trim(),
    });

    if (response.data?.connected === true || response.data?.state === 'open' || response.data?.status === 'open') {
      if (pollingInterval.value) clearInterval(pollingInterval.value);
      await finalizeInboxCreation(response.data?.phone_number || response.data?.instance?.owner);
    } else {
      useAlert('Aguardando leitura do QR Code no celular... Se já leu, aguarde 3 segundos e tente novamente.');
    }
  } catch (err) {
    console.error('Erro ao checar status:', err);
  } finally {
    isLoading.value = false;
  }
};

const startPollingStatus = () => {
  if (pollingInterval.value) clearInterval(pollingInterval.value);

  pollingInterval.value = setInterval(async () => {
    try {
      const response = await uazapiChannelAPI.checkPreviewStatus({
        api_host: apiHost.value.trim(),
        api_key: apiKey.value.trim(),
        instance_name: instanceName.value.trim(),
      });

      if (response.data?.qrcode) {
        qrcodeImage.value = response.data.qrcode;
      }

      if (response.data?.connected === true || response.data?.state === 'open' || response.data?.status === 'open') {
        clearInterval(pollingInterval.value);
        await finalizeInboxCreation(response.data?.phone_number || response.data?.instance?.owner);
      }
    } catch (error) {
      console.warn('Aguardando leitura do QR Code...', error);
    }
  }, 3000);
};

// 4. Cria a Caixa no banco apenas após conexão bem sucedida
const finalizeInboxCreation = async (phoneNumber) => {
  if (pollingInterval.value) clearInterval(pollingInterval.value);
  step.value = 3;
  isLoading.value = true;

  try {
    const response = await uazapiChannelAPI.createInbox({
      name: inboxName.value.trim(),
      api_host: apiHost.value.trim(),
      api_key: apiKey.value.trim(),
      instance_name: instanceName.value.trim(),
      phone_number: phoneNumber,
    });

    createdInbox.value = response.data?.inbox;
    useAlert('WhatsApp conectado e Caixa de Entrada criada com sucesso! 🎉');

    setTimeout(() => {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: {
          page: 'new',
          inbox_id: createdInbox.value.id,
        },
      });
    }, 2000);
  } catch (error) {
    console.error('Erro ao salvar Caixa de Entrada:', error);
    useAlert('WhatsApp autenticado, mas ocorreu um erro ao salvar a caixa.');
  } finally {
    isLoading.value = false;
  }
};

onUnmounted(() => {
  if (pollingInterval.value) clearInterval(pollingInterval.value);
});
</script>

<template>
  <div class="border border-slate-750/40 rounded-2xl p-6 bg-slate-900 shadow-xl max-w-2xl mx-auto my-6 text-slate-100">
    <!-- Header do Canal -->
    <div class="flex items-center gap-3 pb-5 border-b border-slate-800">
      <div class="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-2xl shadow-sm">
        🟢
      </div>
      <div>
        <h2 class="text-base font-bold text-white flex items-center gap-2">
          <span>WhatsApp (UazAPI)</span>
          <span class="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
            Conexão Direta QR Code
          </span>
        </h2>
        <p class="text-xs text-slate-400 mt-0.5">
          Conecte qualquer número de WhatsApp em segundos escaneando o QR Code na tela.
        </p>
      </div>
    </div>

    <!-- PASSO 1: Configuração Inicial -->
    <div v-if="step === 1" class="py-6 space-y-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">
            Nome da Caixa de Entrada <span class="text-rose-400">*</span>
          </label>
          <input
            v-model="inboxName"
            type="text"
            placeholder="Ex: WhatsApp Vendas, Recepção..."
            class="w-full px-3.5 py-2.5 text-xs bg-slate-950 border border-slate-750 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">
            Nome da Instância na UazAPI <span class="text-rose-400">*</span>
          </label>
          <input
            v-model="instanceName"
            type="text"
            placeholder="Ex: IIK4ZN (gerado no painel da UazAPI)"
            class="w-full px-3.5 py-2.5 text-xs bg-slate-950 border border-slate-750 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 font-mono transition-all"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">
            URL da UazAPI <span class="text-rose-400">*</span>
          </label>
          <input
            v-model="apiHost"
            type="text"
            placeholder="https://free.uazapi.com ou seu servidor"
            class="w-full px-3.5 py-2.5 text-xs bg-slate-950 border border-slate-750 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 font-mono transition-all"
          />
        </div>

        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="block text-xs font-semibold text-slate-300">
              Token da Instância <span class="text-rose-400">*</span>
            </label>
            <button
              type="button"
              class="text-[10px] text-emerald-400 hover:underline cursor-pointer"
              @click="showToken = !showToken"
            >
              {{ showToken ? 'Ocultar' : 'Exibir' }}
            </button>
          </div>
          <input
            v-model="apiKey"
            :type="showToken ? 'text' : 'password'"
            placeholder="Token gerado para a instância"
            class="w-full px-3.5 py-2.5 text-xs bg-slate-950 border border-slate-750 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 font-mono transition-all"
          />
        </div>
      </div>

      <div class="pt-4 flex justify-end">
        <button
          type="button"
          :disabled="isLoading"
          class="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer"
          @click="generateQRCode"
        >
          <span v-if="isLoading" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>{{ isLoading ? 'Conectando à UazAPI...' : 'Gerar QR Code ➔' }}</span>
        </button>
      </div>
    </div>

    <!-- PASSO 2: Escanear QR Code -->
    <div v-else-if="step === 2" class="py-6 flex flex-col items-center text-center space-y-4">
      <div class="space-y-1">
        <h3 class="text-sm font-bold text-white">Escaneie o QR Code com seu Celular</h3>
        <p class="text-xs text-slate-400 max-w-md">
          Abra o WhatsApp no celular &gt; <strong>Aparelhos Conectados</strong> &gt; <strong>Conectar um aparelho</strong> e aponte a câmera para a imagem abaixo.
        </p>
      </div>

      <!-- Container do QR Code -->
      <div class="p-4 bg-white rounded-2xl shadow-2xl border-4 border-emerald-500/40 relative">
        <div v-if="qrcodeImage" class="w-56 h-56 flex items-center justify-center">
          <img
            :src="qrcodeImage.startsWith('data:') ? qrcodeImage : `data:image/png;base64,${qrcodeImage}`"
            alt="QR Code WhatsApp"
            class="w-full h-full object-contain"
          />
        </div>
        <div v-else class="w-56 h-56 flex flex-col items-center justify-center text-slate-600 gap-2">
          <div class="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
          <span class="text-xs font-semibold">Carregando QR Code...</span>
        </div>
      </div>

      <!-- Status & Ações -->
      <div class="flex flex-wrap items-center justify-center gap-3 pt-2">
        <button
          type="button"
          :disabled="isLoading"
          class="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
          @click="refreshQRCode"
        >
          <span>🔄</span>
          <span>Atualizar QR Code</span>
        </button>

        <button
          type="button"
          :disabled="isLoading"
          class="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
          @click="checkStatusManually"
        >
          <span>✅</span>
          <span>Já escaneei / Concluir</span>
        </button>

        <div class="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 px-3 py-1.5 rounded-xl border border-amber-500/20">
          <span class="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <span>Aguardando leitura do aparelho...</span>
        </div>
      </div>
    </div>

    <!-- PASSO 3: Sucesso -->
    <div v-else-if="step === 3" class="py-10 flex flex-col items-center text-center space-y-3">
      <div class="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 text-emerald-400 flex items-center justify-center text-3xl animate-bounce">
        ✓
      </div>
      <h3 class="text-base font-bold text-white">WhatsApp Conectado com Sucesso! 🎉</h3>
      <p class="text-xs text-slate-400 max-w-sm">
        Sua instância foi autenticada e a Caixa de Entrada foi criada. Redirecionando para os atendentes...
      </p>
    </div>
  </div>
</template>
